/**
 * Formulartexte: kleines, sicheres Markdown – ohne HTML.
 *
 * Inline: **fett**, *kursiv* oder _kursiv_, ~~durchgestrichen~~, [Link](https://…),
 *         Zeilenumbruch, \* als Escape für Sonderzeichen.
 * Blöcke: Absätze (Leerzeile), Aufzählungen (- …), nummerierte Listen (1. …).
 *
 * Renderer, Builder (Editor) und Mails nutzen denselben Parser, damit überall dasselbe herauskommt.
 */

export type Inline =
  | { t: "text"; v: string }
  | { t: "br" }
  | { t: "b" | "i" | "s"; c: Inline[] }
  | { t: "a"; href: string | null; c: Inline[] };

export type RichBlock = { t: "p"; c: Inline[] } | { t: "ul" | "ol"; items: Inline[][]; start?: number };

const ESCAPABLE = "\\*_~[]()`#.-+";

/** Nur http(s) und mailto – alles andere wird zu reinem Text. */
export function safeHref(href: string): string | null {
  if (href.startsWith("//")) return null;
  try {
    const u = new URL(href, "https://invalid.local");
    return ["http:", "https:", "mailto:"].includes(u.protocol) && /^(https?:|mailto:)/i.test(href) ? href : null;
  } catch {
    return null;
  }
}

const isWord = (ch: string | undefined) => !!ch && /[\p{L}\p{N}]/u.test(ch);

/** Sucht das schließende Zeichen ab `from`, überspringt Escapes; für „*“ werden „**“ übersprungen. */
function findClose(s: string, from: number, delim: string): number {
  for (let i = from; i < s.length; i++) {
    if (s[i] === "\\") {
      i++;
      continue;
    }
    if (!s.startsWith(delim, i)) continue;
    if (delim === "*" && s[i + 1] === "*") {
      // „**…**“ innerhalb von „*…*“ überspringen
      const inner = findClose(s, i + 2, "**");
      if (inner < 0) return -1;
      i = inner + 1;
      continue;
    }
    if (delim === "_" && isWord(s[i + 1])) continue;
    if (i === from) return -1; // leerer Inhalt
    if (/\s/.test(s[i - 1]!)) continue; // „* text *“ ist keine Auszeichnung
    return i;
  }
  return -1;
}

function inlineRec(s: string, depth: number): Inline[] {
  const out: Inline[] = [];
  let buf = "";
  const flush = () => {
    if (buf) out.push({ t: "text", v: buf });
    buf = "";
  };
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "\\" && i + 1 < s.length && ESCAPABLE.includes(s[i + 1]!)) {
      buf += s[++i];
      continue;
    }
    if (ch === "\n") {
      flush();
      out.push({ t: "br" });
      continue;
    }
    if (depth < 6) {
      const delim = s.startsWith("**", i) ? "**" : s.startsWith("~~", i) ? "~~" : ch === "*" ? "*" : ch === "_" && !isWord(s[i - 1]) ? "_" : null;
      if (delim && s[i + delim.length] && !/\s/.test(s[i + delim.length]!)) {
        const end = findClose(s, i + delim.length, delim);
        if (end > 0) {
          flush();
          out.push({ t: delim === "**" ? "b" : delim === "~~" ? "s" : "i", c: inlineRec(s.slice(i + delim.length, end), depth + 1) });
          i = end + delim.length - 1;
          continue;
        }
      }
      if (ch === "[") {
        const close = findBracket(s, i);
        if (close > 0 && s[close + 1] === "(") {
          const paren = s.indexOf(")", close + 2);
          const href = paren > 0 ? s.slice(close + 2, paren) : "";
          if (paren > 0 && href && !/\s/.test(href)) {
            flush();
            out.push({ t: "a", href: safeHref(href), c: inlineRec(s.slice(i + 1, close), depth + 1) });
            i = paren;
            continue;
          }
        }
      }
    }
    buf += ch;
  }
  flush();
  return out;
}

function findBracket(s: string, open: number): number {
  let level = 0;
  for (let i = open; i < s.length; i++) {
    if (s[i] === "\\") {
      i++;
      continue;
    }
    if (s[i] === "[") level++;
    else if (s[i] === "]" && --level === 0) return i;
  }
  return -1;
}

/** Nur Inline-Auszeichnung (für Beschriftungen); Zeilenumbrüche werden zu <br>. */
export const parseInline = (text: string): Inline[] => inlineRec(text, 0);

const BULLET = /^\s{0,3}[-*•]\s+(.*)$/;
const NUMBER = /^\s{0,3}(\d{1,4})[.)]\s+(.*)$/;

/** Absätze und Listen, darin Inline-Auszeichnung. */
export function parseRichText(text: string): RichBlock[] {
  const blocks: RichBlock[] = [];
  let para: string[] = [];
  type Pending = { t: "ul" | "ol"; items: string[]; start?: number };
  let list = null as Pending | null;
  const endPara = () => {
    if (para.length) blocks.push({ t: "p", c: inlineRec(para.join("\n"), 0) });
    para = [];
  };
  const endList = () => {
    const l = list as Pending | null;
    if (l) blocks.push({ t: l.t, items: l.items.map((x) => inlineRec(x, 0)), ...(l.start && l.start !== 1 ? { start: l.start } : {}) });
    list = null;
  };
  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    const b = BULLET.exec(line);
    const n = b ? null : NUMBER.exec(line);
    if (b || n) {
      const t = b ? "ul" : "ol";
      endPara();
      if (list?.t !== t) {
        endList();
        list = { t, items: [], ...(n ? { start: Number(n[1]) } : {}) };
      }
      list!.items.push(b ? b[1]! : n![2]!);
    } else if (!line.trim()) {
      endPara();
      endList();
    } else if (list && /^\s{2,}\S/.test(line)) {
      // eingerückte Fortsetzung eines Listenpunkts
      list.items[list.items.length - 1] += `\n${line.trim()}`;
    } else {
      endList();
      para.push(line);
    }
  }
  endPara();
  endList();
  return blocks;
}

const inlineText = (c: Inline[]): string => c.map((x) => (x.t === "text" ? x.v : x.t === "br" ? "\n" : inlineText(x.c))).join("");

/** Reiner Text ohne Auszeichnung (aria-label, <title>, Klartext-Mails). */
export function richTextPlain(text: string, links: "text" | "url" = "text"): string {
  const one = (c: Inline[]): string =>
    c.map((x) => (x.t === "text" ? x.v : x.t === "br" ? "\n" : x.t === "a" && links === "url" && x.href && inlineText(x.c) !== x.href ? `${one(x.c)} (${x.href})` : one(x.c))).join("");
  return parseRichText(text)
    .map((b) => (b.t === "p" ? one(b.c) : b.items.map((it, i) => `${b.t === "ul" ? "•" : `${(b.start ?? 1) + i}.`} ${one(it)}`).join("\n")))
    .join("\n\n");
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Sicheres HTML (alles escaped) – für Mails und den Editor. */
export function inlineToHtml(c: Inline[], opts: { linkAttrs?: string } = {}): string {
  return c
    .map((x) => {
      switch (x.t) {
        case "text":
          return esc(x.v);
        case "br":
          return "<br>";
        case "b":
          return `<strong>${inlineToHtml(x.c, opts)}</strong>`;
        case "i":
          return `<em>${inlineToHtml(x.c, opts)}</em>`;
        case "s":
          return `<s>${inlineToHtml(x.c, opts)}</s>`;
        case "a":
          return x.href ? `<a href="${esc(x.href)}"${opts.linkAttrs ?? ""}>${inlineToHtml(x.c, opts)}</a>` : inlineToHtml(x.c, opts);
      }
    })
    .join("");
}

export function richTextToHtml(text: string, opts: { linkAttrs?: string; pStyle?: string } = {}): string {
  const p = opts.pStyle ? ` style="${opts.pStyle}"` : "";
  return parseRichText(text)
    .map((b) =>
      b.t === "p"
        ? `<p${p}>${inlineToHtml(b.c, opts)}</p>`
        : `<${b.t}${b.start ? ` start="${b.start}"` : ""}>${b.items.map((it) => `<li>${inlineToHtml(it, opts)}</li>`).join("")}</${b.t}>`,
    )
    .join("");
}

/** Text so escapen, dass er als Markdown wörtlich erscheint. */
export const escapeRichText = (s: string) => s.replace(/[\\*_~[\]]/g, (m) => `\\${m}`);
